/**
 * The phone planner as one scroll (#722).
 *
 * A plan runs seven days from today (#713), so a Thursday signup gets Thursday
 * through next Wednesday. The phone used to page through Monday-to-Sunday
 * weeks: the first screen showed one dinner, the other three of the week sat
 * below it, and next Monday to Wednesday lived on a page reached by a card at
 * the very bottom of the list. The cook who reported it could not tell how
 * many dinners they had or how long the plan ran.
 *
 * Everything here is pure so the rules can be tested without a renderer
 * (`react-native` is a stub manual mock in this repo):
 *
 *   - `buildWeekSections` — which weeks the single scroll stacks, how far it
 *                           may grow, and where "plan the next 7 days" sits
 *   - `mergeFillOnly`     — how that gesture writes without erasing anything
 *   - `isHeroOnlySlot`    — when the list may drop the dish the hero shows
 *
 * Meals still live in Monday week buckets underneath. The stacked list hides
 * that from the cook, not from the code.
 */
import { addDays, weekStartOf } from '../../../utils/localDate';
import { isEntryMeal } from '../../../redux/mealEntry';
import type { PlannedMeal } from '../../../redux/mealPlanSlice';
import { HORIZON_DAYS } from '../../../services/horizonDays';

export interface WeekSection {
  /**
   * The Monday of this block. Every slot key inside it is relative to it.
   *
   * A block always draws its whole week. Later blocks used to stop at their
   * last planned day, so the list ended on food and the days past it could not
   * be planned by hand (#769).
   */
  weekStart: string;
}

export interface WeekSections {
  sections: WeekSection[];
  /**
   * First day of the "plan the next 7 days" gesture, or null when it is not on
   * offer (a week wholly in the past, or a plan with nothing in it — the empty
   * state already offers the first plan). The card sits right above this day.
   */
  extendFrom: string | null;
  /** Whether scrolling to the bottom may add another week. */
  canLoadMore: boolean;
}

/** How far ahead of the page the scroll may reach, in weeks. */
export const MAX_WEEKS_AHEAD = 12;
/** The page's own week plus the weeks after it. */
export const MAX_STACKED_WEEKS = MAX_WEEKS_AHEAD + 1;

/**
 * The weeks one scroll stacks, starting with the week on the page.
 *
 * Every week is drawn whole, empty days included: an empty dinner is the one
 * place to add a dish by hand, so a day the list leaves out is a day nobody
 * can plan (#769). The list reaches at least
 *
 *   - the end of the plan: the weeks after the page that hold food, up to the
 *     first empty one;
 *   - the seventh day from today, when the page holds today or is ahead of it;
 *
 * then one spare week, then one week for every week the cook scrolled into
 * (`loadedWeeks`). Never more than `MAX_WEEKS_AHEAD` after the page.
 */
export function buildWeekSections(
  pageWeek: string,
  today: string,
  mealsForWeek: (weekStart: string) => readonly PlannedMeal[],
  loadedWeeks = 0,
): WeekSections {
  // The plan: the page, then the following weeks while they hold food.
  let lastPlanned = latestDate(mealsForWeek(pageWeek));
  let weeksAhead = 0;
  for (let n = 1; n <= MAX_WEEKS_AHEAD; n++) {
    const latest = latestDate(mealsForWeek(addDays(pageWeek, 7 * n)));
    if (!latest) break;
    lastPlanned = latest;
    weeksAhead = n;
  }

  // Browsing history is not stretched to today; the current week and after are.
  const pageEnd = addDays(pageWeek, 6);
  if (pageEnd >= today) {
    const horizonEnd = addDays(today, HORIZON_DAYS - 1);
    weeksAhead = Math.max(weeksAhead, weeksBetween(pageWeek, weekStartOf(horizonEnd)));
  }
  weeksAhead = Math.min(MAX_WEEKS_AHEAD, weeksAhead + 1 + Math.max(0, loadedWeeks));

  const sections: WeekSection[] = [];
  for (let n = 0; n <= weeksAhead; n++) sections.push({ weekStart: addDays(pageWeek, 7 * n) });
  const canLoadMore = weeksAhead < MAX_WEEKS_AHEAD;

  // The gesture plans forward from today, which a page of history does not reach.
  const planEnd = lastPlanned && lastPlanned > pageEnd ? lastPlanned : pageEnd;
  if (!lastPlanned || planEnd < today) return { sections, extendFrom: null, canLoadMore };

  const dayAfter = addDays(lastPlanned, 1);
  return { sections, extendFrom: dayAfter > today ? dayAfter : today, canLoadMore };
}

/** Whole weeks from one Monday to another. */
function weeksBetween(fromMonday: string, toMonday: string): number {
  let n = 0;
  while (addDays(fromMonday, 7 * (n + 1)) <= toMonday) n++;
  return n;
}

function latestDate(meals: readonly PlannedMeal[]): string | null {
  let latest: string | null = null;
  for (const m of meals) {
    if (!latest || m.scheduledFor > latest) latest = m.scheduledFor;
  }
  return latest;
}

export interface FillOnlyResult {
  /** One `setWeeklyPlan` per touched bucket: everything it held, plus the new dinners. */
  writes: Array<[string, PlannedMeal[]]>;
  /** The dinners that actually went in. */
  added: PlannedMeal[];
}

/**
 * Put generated dinners into the plan without replacing anything.
 *
 * `setWeeklyPlan` replaces a whole bucket, and "the next 7 days" usually
 * straddles a week that already holds food — Thursday to Wednesday lands on
 * the Monday-to-Wednesday a Thursday signup already has. Writing the generated
 * week as-is would erase those three dinners without a word. So each touched
 * bucket is rewritten as everything it already held plus the new dinners, and
 * a dinner is dropped whenever its slot already holds anything at all —
 * including a proposal, which is the household's to settle, not the
 * generator's.
 *
 * Occupancy is read across every bucket, by date: historical writes filed some
 * meals under a bucket that does not match their date, and the list still
 * shows them.
 */
export function mergeFillOnly(
  buckets: Readonly<Record<string, readonly PlannedMeal[]>>,
  incoming: readonly PlannedMeal[],
): FillOnlyResult {
  const taken = new Set<string>();
  for (const meals of Object.values(buckets)) {
    for (const m of meals) taken.add(`${m.scheduledFor}|${m.mealType}`);
  }

  const added: PlannedMeal[] = [];
  const byBucket = new Map<string, PlannedMeal[]>();
  for (const meal of incoming) {
    const key = `${meal.scheduledFor}|${meal.mealType}`;
    if (taken.has(key)) continue;
    taken.add(key);
    added.push(meal);
    const bucket = weekStartOf(meal.scheduledFor);
    const list = byBucket.get(bucket);
    if (list) list.push(meal);
    else byBucket.set(bucket, [meal]);
  }

  const writes: Array<[string, PlannedMeal[]]> = [...byBucket.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([bucket, fresh]) => [bucket, [...(buckets[bucket] ?? []), ...fresh]]);

  return { writes, added };
}

/**
 * True when the hero card already shows everything a slot holds.
 *
 * The hero and the first card of the list used to be the same dinner, one on
 * top of the other. The hero draws the main and, since #746, the
 * accompaniments served with it, so the list drops a slot made of those alone.
 * A slot with a proposal or an entry keeps drawing: the hero shows neither.
 */
export function isHeroOnlySlot(planned: readonly PlannedMeal[], heroMealId: string | null | undefined): boolean {
  return !!heroMealId
    && planned.length > 0
    && planned[0].id === heroMealId
    && planned.every((meal) => meal.mode !== 'proposed' && !isEntryMeal(meal));
}
